import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  chooseAnalyticsConsent,
  getAnalyticsSnapshot,
  startAnalytics,
  subscribeAnalyticsConsent,
} from '../analytics';
import type { AnalyticsConsent as ConsentChoice } from '../analytics';
import '../analytics.css';

export function AnalyticsConsent() {
  const snapshot = useSyncExternalStore(
    subscribeAnalyticsConsent,
    getAnalyticsSnapshot,
    () => 'unavailable' as const,
  );
  const [expanded, setExpanded] = useState(false);
  const panel = useRef<HTMLDialogElement>(null);
  const preferencesButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (snapshot === 'accepted') void startAnalytics();
  }, [snapshot]);

  useEffect(() => {
    if (expanded) panel.current?.focus();
  }, [expanded]);

  if (snapshot === 'unavailable') return null;
  const choice = snapshot;
  const visible = choice === null || expanded;
  const privacyPath = window.location.pathname.startsWith('/spoolmark')
    ? '/spoolmark/privacy#website-analytics'
    : '/eldgard/privacy#website-analytics';

  const saveChoice = (nextChoice: ConsentChoice) => {
    setExpanded(false);
    chooseAnalyticsConsent(nextChoice);
    requestAnimationFrame(() => preferencesButton.current?.focus());
  };

  return (
    <aside
      className="analytics-consent"
      aria-label="Website analytics preferences"
    >
      {visible ? (
        <dialog
          open
          className="analytics-consent-panel"
          aria-labelledby="analytics-consent-title"
          tabIndex={-1}
          ref={panel}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && choice !== null) {
              setExpanded(false);
              requestAnimationFrame(() => preferencesButton.current?.focus());
            }
          }}
        >
          <h2 id="analytics-consent-title">Help us improve this website</h2>
          <p>
            With your permission, Google Analytics uses cookies to measure
            visits and clicks on product and contact links. You can change your
            choice anytime.
          </p>
          <a className="analytics-consent-details" href={privacyPath}>
            Website analytics details
          </a>
          <div className="analytics-consent-actions">
            <button type="button" onClick={() => saveChoice('declined')}>
              Decline analytics
            </button>
            <button type="button" onClick={() => saveChoice('accepted')}>
              Allow analytics
            </button>
          </div>
          {choice !== null && (
            <button
              className="analytics-consent-close"
              type="button"
              onClick={() => {
                setExpanded(false);
                requestAnimationFrame(() => preferencesButton.current?.focus());
              }}
            >
              Close preferences
            </button>
          )}
        </dialog>
      ) : (
        <button
          ref={preferencesButton}
          className="analytics-consent-toggle"
          type="button"
          onClick={() => setExpanded(true)}
        >
          Analytics preferences
        </button>
      )}
    </aside>
  );
}
